import React, { useEffect, useState } from 'react';
import '../../../styles/pages/aiOptimizations/aiOptimizationsAutomated.scss';
import { useTranslation } from 'react-i18next';
import FilterBox from '../../../ui/boxes/FilterBox';
import AIOptimizationsAutomatedCard from '../../../ui/cards/AIOptimizationsAutomatedCard';
import { cursorSVG, eyeSVG } from '../../../lib/allCustomSVGs';
import Popup from '../../../ui/popup/Popup';
import AIOptimizationsAutomatedSettings from './AIOptimizationsAutomatedSettings';
import { AutomatedRecommendationsData } from '../../../interfaces/AIOptimizations';
import get_variant_performance_api_call, {
  VariantPerformanceData,
} from '../../../lib/api/ai-tracker/get_variant_performance_api_call';

interface Props {
  automatedRecommendations: AutomatedRecommendationsData | null;
  onRecommendationToggle: (
    recommendationId: string,
    enabled: boolean
  ) => Promise<void>;
}

interface CSSChange {
  property: string;
  before: string;
  after: string;
}

interface OptimizationItem {
  icon: any;
  active: boolean;
  elementName: string;
  elementDescription: string;
  elementSelector: string;
  cssChanges: CSSChange[];
  conversionRate: string;
  confidence: string;
  visitors: string;
  conversions: string;
  hasMetrics: boolean;
  status: 'Pending' | 'Draft' | 'Running' | 'Paused' | 'Completed';
  recommendationId: string;
  impactMetrics?: VariantPerformanceData | null;
  isLoadingMetrics: boolean;
}

const AIOptimizationsAutomated: React.FC<Props> = ({
  automatedRecommendations,
  onRecommendationToggle,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('All Status');
  const filters = [
    'All Status',
    'Pending',
    'Draft',
    'Running',
    'Paused',
    'Completed',
  ];
  const [isSettingsPopupOpen, setIsSettingsPopupOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editingRecommendationId, setEditingRecommendationId] = useState<
    string | null
  >(null);

  // Convert API data to UI format
  const convertApiDataToOptimizationItems = (): OptimizationItem[] => {
    if (!automatedRecommendations) return [];

    const items: OptimizationItem[] = [];

    // Process active recommendations
    if (
      automatedRecommendations.active &&
      typeof automatedRecommendations.active === 'object'
    ) {
      Object.entries(automatedRecommendations.active).forEach(
        ([elementType, recommendations]) => {
          if (Array.isArray(recommendations)) {
            recommendations.forEach((rec) => {
              const cssChanges: CSSChange[] =
                rec.styles?.map((style: any) => ({
                  property: style.property_type,
                  before: style.current_value,
                  after: style.value,
                })) || [];

              items.push({
                icon: cursorSVG,
                active: true,
                elementName: rec.content_description || rec.element,
                elementDescription: rec.description || '',
                elementSelector: rec.element,
                cssChanges,
                conversionRate: '+0%',
                confidence: '0%',
                visitors: '0',
                conversions: '0',
                hasMetrics: false,
                status: 'Running' as const,
                recommendationId: rec.recommendation_id,
                impactMetrics: null,
                isLoadingMetrics: false,
              });
            });
          }
        }
      );
    }

    // Process pending recommendations
    if (
      automatedRecommendations.pending &&
      typeof automatedRecommendations.pending === 'object'
    ) {
      Object.entries(automatedRecommendations.pending).forEach(
        ([elementType, recommendations]) => {
          if (Array.isArray(recommendations)) {
            recommendations.forEach((rec) => {
              const cssChanges: CSSChange[] =
                rec.styles?.map((style: any) => ({
                  property: style.property_type,
                  before: style.current_value,
                  after: style.value,
                })) || [];

              items.push({
                icon: eyeSVG,
                active: false,
                elementName: rec.content_description || rec.element,
                elementDescription: rec.description || '',
                elementSelector: rec.element,
                cssChanges,
                conversionRate: '+0%',
                confidence: '0%',
                visitors: '0',
                conversions: '0',
                hasMetrics: false,
                status: 'Pending' as const,
                recommendationId: rec.recommendation_id,
                impactMetrics: null,
                isLoadingMetrics: false,
              });
            });
          }
        }
      );
    }

    // Process paused recommendations
    if (Array.isArray(automatedRecommendations.paused)) {
      automatedRecommendations.paused.forEach((rec) => {
        const cssChanges: CSSChange[] =
          rec.styles?.map((style: any) => ({
            property: style.property_type,
            before: style.current_value,
            after: style.value,
          })) || [];

        items.push({
          icon: eyeSVG,
          active: false,
          elementName: rec.content_description || rec.element,
          elementDescription: rec.description || '',
          elementSelector: rec.element,
          cssChanges,
          conversionRate: '+0%',
          confidence: '0%',
          visitors: '0',
          conversions: '0',
          hasMetrics: false,
          status: 'Paused' as const,
          recommendationId: rec.recommendation_id,
          impactMetrics: null,
          isLoadingMetrics: false,
        });
      });
    }

    // Process rejected recommendations
    if (Array.isArray(automatedRecommendations.rejected)) {
      automatedRecommendations.rejected.forEach((rec) => {
        const cssChanges: CSSChange[] =
          rec.styles?.map((style: any) => ({
            property: style.property_type,
            before: style.current_value,
            after: style.value,
          })) || [];

        items.push({
          icon: eyeSVG,
          active: false,
          elementName: rec.content_description || rec.element,
          elementDescription: rec.description || '',
          elementSelector: rec.element,
          cssChanges,
          conversionRate: '+0%',
          confidence: '0%',
          visitors: '0',
          conversions: '0',
          hasMetrics: false,
          status: 'Completed' as const,
          recommendationId: rec.recommendation_id,
          impactMetrics: null,
          isLoadingMetrics: false,
        });
      });
    }

    return items;
  };

  // Function to fetch impact metrics for a recommendation
  const fetchImpactMetrics = async (
    recommendationId: string
  ): Promise<VariantPerformanceData | null> => {
    try {
      const metrics = await get_variant_performance_api_call(recommendationId);
      if (metrics && typeof metrics === 'object') {
        return metrics as VariantPerformanceData;
      }
      return null;
    } catch (error) {
      console.error('Error fetching impact metrics:', error);
      return null;
    }
  };

  // Function to load impact metrics for a specific item
  const loadImpactMetricsForItem = async (item: OptimizationItem) => {
    if (!item.recommendationId || item.impactMetrics) {
      return;
    }

    // Check if already loading in current state
    const currentItem = optimizationData.find(
      (itm) => itm.recommendationId === item.recommendationId
    );
    if (currentItem?.isLoadingMetrics) {
      return;
    }

    // Set loading state
    setOptimizationData((prev) =>
      prev.map((itm) =>
        itm.recommendationId === item.recommendationId
          ? { ...itm, isLoadingMetrics: true }
          : itm
      )
    );

    try {
      const metrics = await fetchImpactMetrics(item.recommendationId);
      if (metrics) {
        const updatedItem = {
          ...item,
          impactMetrics: metrics,
          conversionRate: metrics.conversions_rate?.toString() ?? '+0%',
          confidence: metrics.confidence_metrics?.confidence_level
            ? `${metrics.confidence_metrics.confidence_level}%`
            : '0%',
          visitors: metrics.visitors?.toString() || '0',
          conversions: metrics.conversions?.toString() || '0',
          isLoadingMetrics: false,
        };

        setOptimizationData((prev) =>
          prev.map((itm) =>
            itm.recommendationId === item.recommendationId ? updatedItem : itm
          )
        );
      } else {
        // Reset loading state if no metrics found
        setOptimizationData((prev) =>
          prev.map((itm) =>
            itm.recommendationId === item.recommendationId
              ? { ...itm, isLoadingMetrics: false }
              : itm
          )
        );
      }
    } catch (error) {
      console.error('Error loading impact metrics for item:', error);
      // Reset loading state on error
      setOptimizationData((prev) =>
        prev.map((itm) =>
          itm.recommendationId === item.recommendationId
            ? { ...itm, isLoadingMetrics: false }
            : itm
        )
      );
    }
  };

  const [optimizationData, setOptimizationData] = useState<OptimizationItem[]>(
    convertApiDataToOptimizationItems()
  );

  useEffect(() => {
    const initialItems = convertApiDataToOptimizationItems();

    setOptimizationData(initialItems);

    // Don't automatically load metrics - let the intersection observer handle it
    // when user scrolls to each card
  }, [automatedRecommendations]);

  const filteredData = optimizationData.filter((item) => {
    const matchesSearch = item.elementName
      .toLowerCase()
      .includes(searchQuery.toLowerCase());

    const matchesFilter =
      selectedFilter === 'All Status' || item.status === selectedFilter;

    return matchesSearch && matchesFilter;
  });

  return (
    <div className='aiOptimizationsAutomated'>
      <FilterBox
        extraClassName='aiOptimizationsAutomated-filterBox--identifier'
        filterOptions={filters}
        initialFilter='All Status'
        placeholder={t('SearchAutomatedTests')}
        onSearchChange={(value) => setSearchQuery(value)}
        onFilterChange={(filter) => setSelectedFilter(filter)}
      />
      <div className='aiOptimizationsAutomated__content'>
        {filteredData.map((item, index) => (
          <AIOptimizationsAutomatedCard
            key={index}
            icon={item.icon}
            active={item.active}
            elementName={item.elementName}
            elementDescription={item.elementDescription}
            elementSelector={item.elementSelector}
            cssChanges={item.cssChanges}
            conversionRate={item.conversionRate}
            confidence={item.confidence}
            visitors={item.visitors}
            conversions={item.conversions}
            hasMetrics={item.hasMetrics}
            status={item.status}
            impactMetrics={item.impactMetrics}
            isLoadingMetrics={item.isLoadingMetrics}
            recommendationId={item.recommendationId}
            onLoadMetrics={() => loadImpactMetricsForItem(item)}
            onStatusToggle={async (newStatus) => {
              setOptimizationData((prev) =>
                prev.map((itm, i) =>
                  i === index ? { ...itm, active: newStatus } : itm
                )
              );

              await onRecommendationToggle(item.recommendationId, newStatus);
            }}
            onSettingClick={() => {
              setEditingItem(item.elementName);
              setEditingRecommendationId(item.recommendationId);
              setIsSettingsPopupOpen(true);
              console.log('Editing Item:', item.elementName);
            }}
          />
        ))}
      </div>
      <Popup
        styles={{ backgroundColor: '#fff' }}
        setTrigger={() => setIsSettingsPopupOpen(false)}
        trigger={isSettingsPopupOpen}
      >
        <AIOptimizationsAutomatedSettings
          elementName={editingItem}
          recommendationId={editingRecommendationId || undefined}
          toggleSettingsPopupModal={() => {
            setIsSettingsPopupOpen(!isSettingsPopupOpen);
            setEditingItem(null);
            setEditingRecommendationId(null);
          }}
        />
      </Popup>
    </div>
  );
};

export default AIOptimizationsAutomated;
